import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Header from '../components/layout/Header';
import { useBu } from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useBudgetItems, useLiveActuals, useKpis, useDepartments, useWorkingDays } from '../hooks/useForecast';
import { formatCurrency } from '../utils/formatters';
import apiClient from '../api/apiClient';

const FISCAL_MONTHS = ['Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb'];
const MONTH_KEYS = ['m1','m2','m3','m4','m5','m6','m7','m8','m9','m10','m11','m12'];
const UNIT_OPTIONS = [
  { value: 'currency', label: 'Currency ($)' },
  { value: 'number', label: 'Number' },
  { value: 'percent', label: 'Percent (%)' },
  { value: 'ratio', label: 'Ratio' },
];

// Weekdays remaining in the current calendar month, counting today - used to project the
// rest of the current month's revenue at the existing daily run rate.
function remainingWeekdaysThisMonth() {
  const now = new Date();
  const year = now.getFullYear(), month = now.getMonth() + 1, today = now.getDate();
  const lastDay = new Date(year, month, 0).getDate();
  let count = 0;
  for (let d = today; d <= lastDay; d++) {
    const dow = new Date(year, month - 1, d).getDay();
    if (dow !== 0 && dow !== 6) count++;
  }
  return count;
}

// Weekdays elapsed so far this month (1st through yesterday) - the complement of
// remainingWeekdaysThisMonth(), used to measure real pace-to-date.
function elapsedWeekdaysThisMonth() {
  const now = new Date();
  const year = now.getFullYear(), month = now.getMonth() + 1, today = now.getDate();
  let count = 0;
  for (let d = 1; d < today; d++) {
    const dow = new Date(year, month - 1, d).getDay();
    if (dow !== 0 && dow !== 6) count++;
  }
  return count;
}

function getCurrentFiscalYear() {
  const now = new Date();
  return now.getMonth() >= 2 ? now.getFullYear() : now.getFullYear() - 1;
}

function formatVal(v, unit) {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (isNaN(n)) return '';
  if (unit === 'currency') return formatCurrency(n);
  if (unit === 'percent') return n.toFixed(1) + '%';
  if (unit === 'ratio') return n.toFixed(1);
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function getRowTotal(item) {
  return MONTH_KEYS.reduce((s, k) => s + (Number(item[k]) || 0), 0);
}

function getDollarChange(item) {
  const total = getRowTotal(item);
  const py = Number(item.prior_year) || 0;
  return total - py;
}

function getPctChange(item) {
  const total = getRowTotal(item);
  const py = Number(item.prior_year) || 0;
  if (py === 0) return null;
  return ((total - py) / py) * 100;
}

// Group items by department
function groupByDept(items) {
  const groups = {};
  const deptOrder = [];
  for (const item of items) {
    if (!groups[item.department]) {
      groups[item.department] = [];
      deptOrder.push(item.department);
    }
    groups[item.department].push(item);
  }
  return { groups, deptOrder };
}

// ── Editable Cell ────────────────────────────────────────────────────────────
function EditableCell({ value, onSave, unit, style }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  const startEdit = () => {
    setVal(value || '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const n = Number(val);
    if (!isNaN(n) && n !== Number(value || 0)) {
      onSave(n);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{ ...st.cellInput, ...style }}
      />
    );
  }

  return (
    <div onClick={startEdit} style={{ ...st.cellVal, ...style, cursor: 'pointer' }} title="Click to edit">
      {formatVal(value, unit) || '\u2014'}
    </div>
  );
}

// ── Add Line Item Form ───────────────────────────────────────────────────────
function AddLineForm({ department, fiscalYear, onAdd, onCancel }) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('currency');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onAdd({ fiscal_year: fiscalYear, department, line_name: name.trim(), unit });
      onCancel();
    } catch {} finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0.75rem', background: '#f8fafc', borderRadius: '0.375rem' }}>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Line item name" style={st.input} autoFocus />
      <select value={unit} onChange={e => setUnit(e.target.value)} style={{ ...st.input, width: '120px' }}>
        {UNIT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <button type="submit" disabled={saving} style={st.addBtn}>{saving ? '...' : 'Add'}</button>
      <button type="button" onClick={onCancel} style={st.cancelBtn}>Cancel</button>
    </form>
  );
}

// ── Add Department Form ──────────────────────────────────────────────────────
function AddDeptForm({ fiscalYear, onAdd, onCancel }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onAdd({ fiscal_year: fiscalYear, department: name.trim(), line_name: 'Revenue', unit: 'currency' });
      onCancel();
    } catch {} finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Department name" style={st.input} autoFocus />
      <button type="submit" disabled={saving} style={st.addBtn}>{saving ? '...' : 'Add Department'}</button>
      <button type="button" onClick={onCancel} style={st.cancelBtn}>Cancel</button>
    </form>
  );
}

// ── Department Section ───────────────────────────────────────────────────────
function DepartmentSection({ department, items, fiscalYear, onUpdateItem, onAddItem, onDeleteItem }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showAddLine, setShowAddLine] = useState(false);

  // Compute department totals for each month
  const revenueRows = items.filter(it => it.unit === 'currency');
  const monthTotals = MONTH_KEYS.map(k => revenueRows.reduce((s, it) => s + (Number(it[k]) || 0), 0));
  const deptTotal = monthTotals.reduce((s, v) => s + v, 0);
  // deptPY used in grand total calculation
  const deptPY = revenueRows.reduce((s, it) => s + (Number(it.prior_year) || 0), 0); // eslint-disable-line no-unused-vars

  return (
    <div style={st.deptSection}>
      <div style={st.deptHeader} onClick={() => setCollapsed(!collapsed)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#6366f1' }}>{collapsed ? '\u25B6' : '\u25BC'}</span>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>{department}</h3>
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>({items.length} items)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {deptTotal > 0 && <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#1e293b' }}>{formatCurrency(deptTotal)}</span>}
          <button onClick={e => { e.stopPropagation(); setShowAddLine(true); }} style={st.smallBtn}>+ Line</button>
        </div>
      </div>

      {!collapsed && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={{ ...st.th, minWidth: '150px', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1 }}>KPI</th>
                  {FISCAL_MONTHS.map(m => <th key={m} style={st.th}>{m}</th>)}
                  <th style={{ ...st.th, fontWeight: '700' }}>Total</th>
                  <th style={st.th}>PY</th>
                  <th style={st.th}>$ Chg</th>
                  <th style={st.th}>% Chg</th>
                  <th style={{ ...st.th, width: '30px' }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const total = getRowTotal(item);
                  const dollarChg = getDollarChange(item);
                  const pctChg = getPctChange(item);
                  return (
                    <tr key={item.id} style={idx % 2 !== 0 ? { background: '#f8fafc' } : {}}>
                      <td style={{ ...st.td, fontWeight: '600', position: 'sticky', left: 0, background: idx % 2 !== 0 ? '#f8fafc' : '#fff', zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span>{item.line_name}</span>
                          <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                            {item.unit === 'percent' ? '%' : item.unit === 'currency' ? '$' : item.unit === 'ratio' ? 'x' : '#'}
                          </span>
                        </div>
                      </td>
                      {MONTH_KEYS.map(k => (
                        <td key={k} style={st.td}>
                          <EditableCell
                            value={item[k]}
                            unit={item.unit}
                            onSave={v => onUpdateItem(item.id, { [k]: v })}
                          />
                        </td>
                      ))}
                      <td style={{ ...st.td, fontWeight: '700', background: '#f0fdf4' }}>
                        {formatVal(item.unit === 'percent' || item.unit === 'ratio' ? total / 12 : total, item.unit)}
                      </td>
                      <td style={st.td}>
                        <EditableCell
                          value={item.prior_year}
                          unit={item.unit}
                          onSave={v => onUpdateItem(item.id, { prior_year: v })}
                        />
                      </td>
                      <td style={{ ...st.td, color: dollarChg >= 0 ? '#22c55e' : '#dc2626', fontWeight: '600' }}>
                        {item.unit === 'currency' ? formatVal(dollarChg, 'currency') : formatVal(dollarChg, item.unit)}
                      </td>
                      <td style={{ ...st.td, color: pctChg === null ? '#94a3b8' : pctChg >= 0 ? '#22c55e' : '#dc2626', fontWeight: '600' }}>
                        {pctChg === null ? '\u2014' : (pctChg >= 0 ? '+' : '') + pctChg.toFixed(1) + '%'}
                      </td>
                      <td style={st.td}>
                        <button onClick={() => { if (window.confirm(`Delete "${item.line_name}"?`)) onDeleteItem(item.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.8rem' }} title="Delete">x</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {showAddLine && (
            <div style={{ marginTop: '0.5rem' }}>
              <AddLineForm department={department} fiscalYear={fiscalYear} onAdd={onAddItem} onCancel={() => setShowAddLine(false)} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Month Drill-Down Panel ─────────────────────────────────────────────────
function MonthDrillDown({ fiscalYear, calMonth, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('day'); // day or week

  // Calculate the calendar year for this fiscal month
  const calYear = calMonth >= 3 ? fiscalYear : fiscalYear + 1;
  const monthName = FISCAL_MONTHS[calMonth >= 3 ? calMonth - 3 : calMonth + 9];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const startDate = `${calYear}-${String(calMonth).padStart(2, '0')}-01`;
    const lastDay = new Date(calYear, calMonth, 0).getDate();
    const endDate = `${calYear}-${String(calMonth).padStart(2, '0')}-${lastDay}`;

    apiClient.get('/win-the-day/live/' + startDate)
      .then(() => {
        // Fetch live data for each day of the month
        const days = [];
        for (let d = 1; d <= lastDay; d++) {
          days.push(`${calYear}-${String(calMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
        }
        return Promise.allSettled(
          days.map(date => apiClient.get(`/win-the-day/live/${date}`).then(r => ({ date, ...r.data })))
        );
      })
      .then(results => {
        if (cancelled) return;
        const dailyData = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value);
        setData(dailyData);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [calYear, calMonth]);

  if (loading) return <div style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8' }}>Loading daily data for {monthName}...</div>;
  if (!data || data.length === 0) return <div style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8' }}>No data available</div>;

  // Group by week if needed
  const displayData = viewMode === 'week' ? (() => {
    const weeks = [];
    let currentWeek = { label: 'Week 1', revenue: 0, completed: 0, scheduled: 0, totalJobs: 0, days: 0 };
    let weekNum = 1;
    for (const d of data) {
      const dayOfWeek = new Date(d.date + 'T00:00:00').getDay();
      if (dayOfWeek === 1 && currentWeek.days > 0) {
        weeks.push(currentWeek);
        weekNum++;
        currentWeek = { label: `Week ${weekNum}`, revenue: 0, completed: 0, scheduled: 0, totalJobs: 0, days: 0 };
      }
      currentWeek.revenue += d.revenue || d.completedRevenue || 0;
      currentWeek.completed += d.completed || 0;
      currentWeek.scheduled += d.scheduled || 0;
      currentWeek.totalJobs += d.totalJobs || 0;
      currentWeek.days++;
    }
    if (currentWeek.days > 0) weeks.push(currentWeek);
    return weeks;
  })() : data;

  return (
    <div style={{ background: '#fefce8', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #fde68a', marginTop: '0.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#92400e' }}>{monthName} {calYear} — Daily Breakdown</span>
          <button onClick={() => setViewMode('day')} style={{ ...st.smallBtn, ...(viewMode === 'day' ? { background: '#6366f1', color: '#fff' } : {}) }}>Day</button>
          <button onClick={() => setViewMode('week')} style={{ ...st.smallBtn, ...(viewMode === 'week' ? { background: '#6366f1', color: '#fff' } : {}) }}>Week</button>
        </div>
        <button onClick={onClose} style={{ ...st.smallBtn, color: '#dc2626' }}>✕ Close</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={st.table}>
          <thead><tr>
            <th style={st.th}>{viewMode === 'day' ? 'Date' : 'Period'}</th>
            <th style={st.th}>Revenue</th>
            <th style={st.th}>Completed</th>
            <th style={st.th}>Scheduled</th>
            <th style={st.th}>Total Jobs</th>
            <th style={st.th}>Avg Ticket</th>
          </tr></thead>
          <tbody>
            {displayData.map((d, idx) => {
              const rev = viewMode === 'day' ? (d.revenue || d.completedRevenue || 0) : d.revenue;
              const comp = viewMode === 'day' ? (d.completed || 0) : d.completed;
              const sched = viewMode === 'day' ? (d.scheduled || 0) : d.scheduled;
              const total = viewMode === 'day' ? (d.totalJobs || 0) : d.totalJobs;
              const avg = comp > 0 ? rev / comp : 0;
              const label = viewMode === 'day'
                ? new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                : d.label;
              const isWeekend = viewMode === 'day' && [0, 6].includes(new Date(d.date + 'T00:00:00').getDay());
              return (
                <tr key={idx} style={{ background: isWeekend ? '#f8fafc' : idx % 2 ? '#fffbeb' : undefined }}>
                  <td style={{ ...st.td, fontWeight: '600', textAlign: 'left' }}>{label}</td>
                  <td style={{ ...st.td, color: rev > 0 ? '#16a34a' : '#cbd5e1', fontWeight: '600' }}>{rev > 0 ? formatCurrency(rev) : '—'}</td>
                  <td style={{ ...st.td, color: comp > 0 ? '#22c55e' : '#cbd5e1' }}>{comp || '—'}</td>
                  <td style={{ ...st.td, color: sched > 0 ? '#2563eb' : '#cbd5e1' }}>{sched || '—'}</td>
                  <td style={st.td}>{total || '—'}</td>
                  <td style={{ ...st.td, color: avg > 0 ? '#7c3aed' : '#cbd5e1' }}>{avg > 0 ? formatCurrency(avg) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Forecast BU Table (reusable for each BU and total) ─────────────────────
function ForecastTable({ label, fiscalMonthMap, budgetByMonth, currentFM, color, workingDays, onMonthClick }) {
  const allRevenue = Array.from({ length: 12 }, (_, i) => fiscalMonthMap[i + 1]?.liveRevenue || 0).reduce((s, v) => s + v, 0);
  const allScheduled = Array.from({ length: 12 }, (_, i) => fiscalMonthMap[i + 1]?.liveScheduledJobs || 0).reduce((s, v) => s + v, 0);
  const allCompleted = Array.from({ length: 12 }, (_, i) => fiscalMonthMap[i + 1]?.liveCompletedJobs || 0).reduce((s, v) => s + v, 0);
  const allJobs = Array.from({ length: 12 }, (_, i) => fiscalMonthMap[i + 1]?.liveJobCount || 0).reduce((s, v) => s + v, 0);
  const allScheduledRevenue = Array.from({ length: 12 }, (_, i) => fiscalMonthMap[i + 1]?.liveScheduledRevenue || 0).reduce((s, v) => s + v, 0);
  // Avg ticket based on completed jobs and actual revenue
  const avgTicket = allCompleted > 0 ? allRevenue / allCompleted : 0;

  // The current month's real daily pace-to-date (revenue booked so far / working days
  // elapsed so far this month). A pure actual-rate is noisy early in the month - 2 elapsed
  // days means one unusually big or small day swings the whole rest-of-month projection - so
  // blend it with the budget-derived rate, weighted by how far into the month we are: mostly
  // budget on day 1, shifting to mostly-actual by month end as more real data accumulates.
  const elapsedDays = elapsedWeekdaysThisMonth();
  const currentMonthLive = fiscalMonthMap[currentFM];
  const currentCalMonth = new Date().getMonth() + 1;
  const totalWorkingDaysThisMonth = workingDays?.[currentCalMonth] || 0;
  const currentMonthActualRate = elapsedDays > 0 && currentMonthLive
    ? (currentMonthLive.liveRevenue || 0) / elapsedDays
    : null;
  const budgetRateThisMonth = totalWorkingDaysThisMonth > 0
    ? (budgetByMonth[currentFM - 1] || 0) / totalWorkingDaysThisMonth
    : 0;
  const blendWeight = totalWorkingDaysThisMonth > 0 ? Math.min(1, elapsedDays / totalWorkingDaysThisMonth) : 0;
  const blendedDailyRate = currentMonthActualRate !== null
    ? currentMonthActualRate * blendWeight + budgetRateThisMonth * (1 - blendWeight)
    : budgetRateThisMonth;
  // The rest-of-month projection was multiplying blendedDailyRate by a naive Mon-Fri calendar
  // count (remainingWeekdaysThisMonth) instead of the real configured working-days total from
  // the Working Days tab - those two can diverge (holidays, planned closures), and blendWeight
  // above already uses the real total, so the projection should stay consistent with it rather
  // than mixing a naive count into one part of the calculation and a real one into another.
  const remainingWorkingDaysThisMonth = totalWorkingDaysThisMonth > 0
    ? Math.max(0, totalWorkingDaysThisMonth - elapsedDays)
    : remainingWeekdaysThisMonth();

  return (
    <div style={{ ...st.deptSection, borderLeft: `3px solid ${color || '#6366f1'}` }}>
      <div style={{ padding: '0.75rem 1rem', background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', borderBottom: '1px solid #c7d2fe', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' }}>{label}</h4>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Avg Ticket: <b style={{ color: '#1e293b' }}>{avgTicket > 0 ? formatCurrency(avgTicket) : '—'}</b></span>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Revenue: <b style={{ color: '#22c55e' }}>{formatCurrency(allRevenue)}</b></span>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Jobs: <b style={{ color: '#2563eb' }}>{allJobs}</b> (Completed: {allCompleted}, Scheduled: {allScheduled})</span>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Scheduled Revenue: <b style={{ color: '#7c3aed' }}>{allScheduledRevenue > 0 ? formatCurrency(allScheduledRevenue) : '—'}</b></span>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={st.table}>
          <thead><tr>
            <th style={{ ...st.th, minWidth: '120px' }}>Metric</th>
            {FISCAL_MONTHS.map((m, i) => {
              const calMonth = i < 10 ? i + 3 : i - 9;
              return <th key={m} onClick={() => onMonthClick && onMonthClick(calMonth)} style={{ ...st.th, background: i + 1 === currentFM ? '#ede9fe' : '#f8fafc', cursor: onMonthClick ? 'pointer' : 'default' }} title="Click to drill down">{m}</th>;
            })}
            <th style={{ ...st.th, fontWeight: '700' }}>Total</th>
          </tr></thead>
          <tbody>
            <tr>
              <td style={{ ...st.td, fontWeight: '600' }}>Budget</td>
              {budgetByMonth.map((v, i) => <td key={i} style={st.td}>{v > 0 ? formatCurrency(v) : '—'}</td>)}
              <td style={{ ...st.td, fontWeight: '700' }}>{formatCurrency(budgetByMonth.reduce((s, v) => s + v, 0))}</td>
            </tr>
            {workingDays && (
              <>
                <tr style={{ background: '#fffbeb' }}>
                  <td style={{ ...st.td, fontWeight: '600', color: '#92400e', fontSize: '0.75rem' }}>Working Days</td>
                  {FISCAL_MONTHS.map((_, i) => {
                    const calMonth = i < 10 ? i + 3 : i - 9;
                    const wd = workingDays[calMonth] || 0;
                    return <td key={i} style={{ ...st.td, fontSize: '0.75rem', color: '#92400e' }}>{wd || '—'}</td>;
                  })}
                  <td style={{ ...st.td, fontWeight: '700', fontSize: '0.75rem', color: '#92400e' }}>
                    {Object.values(workingDays).reduce((s, v) => s + (v || 0), 0)}
                  </td>
                </tr>
                <tr style={{ background: '#fefce8' }}>
                  <td style={{ ...st.td, fontWeight: '600', color: '#a16207', fontSize: '0.75rem' }}>Daily Run Rate</td>
                  {FISCAL_MONTHS.map((_, i) => {
                    const calMonth = i < 10 ? i + 3 : i - 9;
                    const wd = workingDays[calMonth] || 0;
                    const budgetRate = wd > 0 ? budgetByMonth[i] / wd : 0;
                    // Current month: blended actual/budget rate (see blendedDailyRate above),
                    // not a pure day-1-noisy actual rate or a rate frozen at the budget assumption.
                    const rate = (i + 1 === currentFM) ? blendedDailyRate : budgetRate;
                    return <td key={i} style={{ ...st.td, fontSize: '0.75rem', color: rate > 0 ? '#a16207' : '#cbd5e1' }}>{rate > 0 ? formatCurrency(rate) : '—'}</td>;
                  })}
                  <td style={st.td}></td>
                </tr>
              </>
            )}
            <tr style={{ background: '#f8fafc' }}>
              <td style={{ ...st.td, fontWeight: '600' }}>Actual Revenue</td>
              {MONTH_KEYS.map((_, i) => { const v = fiscalMonthMap[i + 1]?.liveRevenue || 0; return <td key={i} style={{ ...st.td, color: v > 0 ? '#6366f1' : '#cbd5e1', fontWeight: '600' }}>{v > 0 ? formatCurrency(v) : '—'}</td>; })}
              <td style={{ ...st.td, fontWeight: '700', color: '#6366f1' }}>{formatCurrency(allRevenue)}</td>
            </tr>
            <tr>
              <td style={{ ...st.td, fontWeight: '600' }}>Completed Jobs</td>
              {MONTH_KEYS.map((_, i) => { const v = fiscalMonthMap[i + 1]?.liveCompletedJobs || 0; return <td key={i} style={{ ...st.td, color: v > 0 ? '#22c55e' : '#cbd5e1' }}>{v > 0 ? v : '—'}</td>; })}
              <td style={{ ...st.td, fontWeight: '700', color: '#22c55e' }}>{allCompleted}</td>
            </tr>
            <tr style={{ background: '#f8fafc' }}>
              <td style={{ ...st.td, fontWeight: '600' }}>Scheduled Jobs</td>
              {MONTH_KEYS.map((_, i) => { const v = fiscalMonthMap[i + 1]?.liveScheduledJobs || 0; return <td key={i} style={{ ...st.td, color: v > 0 ? '#2563eb' : '#cbd5e1' }}>{v > 0 ? v : '—'}</td>; })}
              <td style={{ ...st.td, fontWeight: '700', color: '#2563eb' }}>{allScheduled}</td>
            </tr>
            <tr>
              <td style={{ ...st.td, fontWeight: '600' }}>Scheduled Revenue</td>
              {MONTH_KEYS.map((_, i) => { const v = fiscalMonthMap[i + 1]?.liveScheduledRevenue || 0; return <td key={i} style={{ ...st.td, color: v > 0 ? '#7c3aed' : '#cbd5e1', fontWeight: '600' }}>{v > 0 ? formatCurrency(v) : '—'}</td>; })}
              <td style={{ ...st.td, fontWeight: '700', color: '#7c3aed' }}>{formatCurrency(allScheduledRevenue)}</td>
            </tr>
            <tr style={{ background: '#fef9c3' }}>
              <td style={{ ...st.td, fontWeight: '700', color: '#92400e' }}>Forecast</td>
              {MONTH_KEYS.map((_, i) => {
                const fm = i + 1; const live = fiscalMonthMap[fm];
                let val;
                if (fm < currentFM) val = live?.liveRevenue || 0; // Past months: use actual revenue
                // Current month: completed + already-scheduled revenue (known so far), PLUS a
                // projection for the rest of the month - remaining weekdays * the blended
                // actual/budget daily rate (see blendedDailyRate above).
                else if (fm === currentFM) {
                  val = (live?.liveRevenue || 0) + (live?.liveScheduledRevenue || 0) + blendedDailyRate * remainingWorkingDaysThisMonth;
                }
                else val = budgetByMonth[i] || 0; // Future months: use budget
                return <td key={i} style={{ ...st.td, fontWeight: '600', color: '#92400e' }}>{formatCurrency(val)}</td>;
              })}
              <td style={{ ...st.td, fontWeight: '700', color: '#92400e' }}>
                {formatCurrency(MONTH_KEYS.reduce((s, _, i) => {
                  const fm = i + 1; const live = fiscalMonthMap[fm];
                  if (fm < currentFM) return s + (live?.liveRevenue || 0);
                  if (fm === currentFM) {
                    return s + (live?.liveRevenue || 0) + (live?.liveScheduledRevenue || 0) + blendedDailyRate * remainingWorkingDaysThisMonth;
                  }
                  return s + (budgetByMonth[i] || 0);
                }, 0))}
              </td>
            </tr>
            <tr style={{ background: '#f0fdf4' }}>
              <td style={{ ...st.td, fontWeight: '700', color: '#166534' }}>Variance</td>
              {MONTH_KEYS.map((_, i) => {
                const fm = i + 1; const actual = fiscalMonthMap[fm]?.liveRevenue || 0; const bgt = budgetByMonth[i];
                if (fm >= currentFM || actual === 0) return <td key={i} style={{ ...st.td, color: '#cbd5e1' }}>—</td>;
                const diff = actual - bgt;
                return <td key={i} style={{ ...st.td, fontWeight: '600', color: diff >= 0 ? '#22c55e' : '#dc2626' }}>{(diff >= 0 ? '+' : '') + formatCurrency(diff)}</td>;
              })}
              <td style={st.td}></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Forecast Section ─────────────────────────────────────────────────────────
function ForecastSection({ fiscalYear: parentFY, budgetItems: parentBudgetItems }) {
  const currentFY = getCurrentFiscalYear();
  const [fiscalYear, setFiscalYear] = useState(parentFY);
  const [drillDownMonth, setDrillDownMonth] = useState(null);
  const { items: ownBudgetItems, loading: budgetLoading } = useBudgetItems(fiscalYear);
  const budgetItems = fiscalYear === parentFY ? parentBudgetItems : ownBudgetItems;
  const { data: liveData, loading: liveLoading, byBu: liveByBu, error: liveError } = useLiveActuals(fiscalYear);
  const { data: workingDays } = useWorkingDays(fiscalYear);
  const fyOptions = [currentFY + 1, currentFY, currentFY - 1, currentFY - 2];

  // Map live data to fiscal months
  const fiscalMonthMap = useMemo(() => {
    const map = {};
    for (const ld of liveData) {
      let fm; if (ld.month >= 3) fm = ld.month - 2; else fm = ld.month + 10;
      map[fm] = ld;
    }
    return map;
  }, [liveData]);

  // Build per-BU fiscal month maps
  const buFiscalMaps = useMemo(() => {
    if (!liveByBu) return {};
    const maps = {};
    for (const [buName, buMonths] of Object.entries(liveByBu)) {
      const map = {};
      for (const ld of buMonths) {
        let fm; if (ld.month >= 3) fm = ld.month - 2; else fm = ld.month + 10;
        map[fm] = ld;
      }
      maps[buName] = map;
    }
    // Combine specific raw ST business units into one table under a friendlier label, per
    // request - e.g. Gold Plans (membership billing) folded into HVAC Service, HVAC Sales
    // folded into Comfort Advisors, since these are managed together in practice. Listed
    // source names include the real raw variants seen live (e.g. "HVAC - Service." with a
    // trailing period, "Gold Plans" plural) plus a couple of plausible alternates.
    for (const [targetLabel, sourceNames] of Object.entries(COMBINE_GROUPS)) {
      const present = sourceNames.filter(n => maps[n]);
      if (present.length === 0) continue;
      const merged = {};
      for (let fm = 1; fm <= 12; fm++) {
        const combined = { month: fm, liveRevenue: 0, liveJobCount: 0, liveScheduledJobs: 0, liveScheduledRevenue: 0, liveCompletedJobs: 0 };
        for (const n of present) {
          const ld = maps[n][fm];
          if (!ld) continue;
          combined.liveRevenue += ld.liveRevenue || 0;
          combined.liveJobCount += ld.liveJobCount || 0;
          combined.liveScheduledJobs += ld.liveScheduledJobs || 0;
          combined.liveScheduledRevenue += ld.liveScheduledRevenue || 0;
          combined.liveCompletedJobs += ld.liveCompletedJobs || 0;
        }
        merged[fm] = combined;
      }
      for (const n of present) delete maps[n];
      maps[targetLabel] = merged;
    }
    return maps;
  }, [liveByBu]);

  // Current fiscal month
  const now = new Date();
  const calMonth = now.getMonth() + 1;
  const currentFM = calMonth >= 3 ? calMonth - 2 : calMonth + 10;

  // Budget by department for BU matching
  const { groups: budgetGroups } = useMemo(() => groupByDept(budgetItems), [budgetItems]);

  // Overall budget by month
  const revenueItems = budgetItems.filter(it => it.unit === 'currency');
  const budgetByMonth = MONTH_KEYS.map(k => revenueItems.reduce((s, it) => s + (Number(it[k]) || 0), 0));

  // Get BU names from live data
  const buNames = useMemo(() => Object.keys(buFiscalMaps).sort(), [buFiscalMaps]);

  const BU_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ ...st.forecastSection, padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
            Forecast: Completed Revenue + Scheduled Jobs × Avg Ticket
          </h3>
          <select value={fiscalYear} onChange={e => setFiscalYear(Number(e.target.value))} style={{ ...st.input, width: 'auto' }}>
            {fyOptions.map(y => <option key={y} value={y}>FY {y} (Mar {y} - Feb {y + 1})</option>)}
          </select>
        </div>
      </div>
      {(liveLoading || budgetLoading) && <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', padding: '0 1rem' }}>Loading live data...</span>}
      {liveError && <ErrorBanner message={liveError} />}

      {/* Per-BU Forecast Tables */}
      {buNames.map((buName, idx) => {
        // Try to match BU name to a budget department. Previously an exact string match
        // (budgetGroups[d]), which silently failed whenever the Budget tab department name
        // didn't spell the BU out in full - e.g. real BU "HVAC - Install" vs Budget tab
        // department "Install" - so that table's Budget row (and everything derived from it:
        // Forecast, Variance) always showed $0 despite real revenue. Use the same fuzzy
        // matcher the KPI tab already uses, tried against every raw source BU name a combined
        // table absorbed plus the combined label itself.
        const sourceDepts = COMBINE_GROUPS[buName] ? [...COMBINE_GROUPS[buName], buName] : [buName];
        const matchedDeptNames = Object.keys(budgetGroups).filter(dept =>
          sourceDepts.some(src => deptMatchesBuFuzzy(dept, src))
        );
        const deptItems = matchedDeptNames.flatMap(d => budgetGroups[d] || []);
        const deptRevItems = deptItems.filter(it => it.unit === 'currency');
        const deptBudget = MONTH_KEYS.map(k => deptRevItems.reduce((s, it) => s + (Number(it[k]) || 0), 0));
        return (
          <ForecastTable
            key={buName}
            label={buName}
            fiscalMonthMap={buFiscalMaps[buName] || {}}
            budgetByMonth={deptBudget}
            currentFM={currentFM}
            color={BU_COLORS[idx % BU_COLORS.length]}
            workingDays={workingDays}
            onMonthClick={setDrillDownMonth}
          />
        );
      })}

      {/* Grand Total */}
      <ForecastTable
        label="📊 Total — All Business Units"
        fiscalMonthMap={fiscalMonthMap}
        budgetByMonth={budgetByMonth}
        currentFM={currentFM}
        color="#1e293b"
        workingDays={workingDays}
        onMonthClick={setDrillDownMonth}
      />

      {/* Month Drill-Down */}
      {drillDownMonth && (
        <MonthDrillDown
          fiscalYear={fiscalYear}
          calMonth={drillDownMonth}
          onClose={() => setDrillDownMonth(null)}
        />
      )}
    </div>
  );
}

// Helper: map a budget line item to the correct live data field
// deptLive = this department's own BU-matched actuals (revenue/jobs), overallLive = the
// unscoped company-wide totals. Leads/opportunities have no per-BU breakdown at all (GHL
// contacts/opportunities aren't tied to a ServiceTitan business unit anywhere in this data
// model), so those always come from overallLive regardless of department.
function getActualForItem(item, deptLive, overallLive, membershipStats, leadStats, serviceCallStats) {
  const name = (item.line_name || '').toLowerCase();
  // The new "Call Center" budget department (Total Bookings/Booked Calls/Booking Rate/Lead
  // Calls/Total Calls/Emails/Inbound/Outbound/Unique Calls, imported from the user's real
  // spreadsheet) has no live-actuals wiring on this tab at all yet (only Win the Day tracks
  // Inbound/Outbound via real ST calls) - but "Lead Calls" and "Total Calls/Emails" happened to
  // contain the substrings "lead" and "total call", which are meant for completely different
  // KPIs (Marketed/Tech Leads, HVAC Service's real call-type breakdown) and were silently
  // grabbing those unrelated numbers instead of showing honest "no data yet". Confirmed live:
  // "Lead Calls" showed the flat GHL contact count (5,000), "Total Calls/Emails" showed HVAC
  // Service's real Total Calls figure - neither has anything to do with Call Center.
  if (item.department === 'Call Center') return 0;
  // Real membership data (Portfolio, Net New Adds, RMR) is a completely separate system from
  // the ST job/revenue data every other branch below uses - previously these fell through to
  // the generic branches and showed nonsense (e.g. "Net New Adds" was a raw ST job count for
  // Membership-BU-matched jobs, nothing to do with actual new membership signups). Only passed
  // for a department whose name matches "membership" - see caller.
  if (membershipStats) {
    if (name.includes('portfolio')) return membershipStats.totalPortfolio || 0;
    if (name.includes('rmr')) return membershipStats.mrr || 0;
    if (name.includes('attrition')) {
      return membershipStats.totalPortfolio > 0
        ? (membershipStats.canceledMemberships / membershipStats.totalPortfolio) * 100
        : 0;
    }
    if (name.includes('net new add')) return membershipStats.newMemberships || 0;
    // Channel-specific adds classified by the real sold_by_name on each membership sale (see
    // /membership-stats/:year in server/routes/forecast.js) - real employees added via the
    // Teams page (with a business unit assigned) drive this automatically once populated;
    // until then it falls back to a manually-confirmed seller roster. Tech/Plumbing show 0
    // until a real seller lands in one of those channels, rather than guessing.
    if (name.includes('sales add')) return membershipStats.salesAdds || 0;
    if (name.includes('call centre add') || name.includes('call center add')) return membershipStats.callCentreAdds || 0;
    if (name.includes('tech add')) return membershipStats.techAdds || 0;
    if (name.includes('plumbing add')) return membershipStats.plumbingAdds || 0;
    if (name.includes('add')) return 0;
  }
  // Real "Marketed Leads"/"Tech Leads" counts + their close rates, from the two real ST
  // reports confirmed via live diagnostics (Marketing Leads Per Business Unit / Lead
  // Generation) - see getLeadStats in server/routes/forecast.js. Previously every KPI whose
  // name contained "lead" (including the L2C% close-rate lines) fell through to the same
  // flat GHL contact count regardless of which one it actually was.
  if (leadStats) {
    // "Tech Conversion" (HVAC Service dept) is a different literal name than "Tech L2C%"
    // (Walker Leads dept) for the same real concept - it never matched the l2c/close+rate
    // check below at all, so it always fell through to 0 regardless of real data.
    if (name.includes('l2c') || (name.includes('close') && name.includes('rate')) || name === 'tech conversion') {
      if (name.includes('market')) return leadStats.marketedCloseRate || 0;
      if (name.includes('tech')) return leadStats.techCloseRate || 0;
    }
    if (name.includes('lead')) {
      if (name.includes('market') || name.includes('mrktd')) return leadStats.marketedLeads || 0;
      if (name.includes('tech')) return leadStats.techLeads || 0;
      // "Total Leads" (HVAC Sales Team dept) has no separate real source of its own - the
      // closest real number is the same HVAC - Sales marketed-lead count used above, better
      // than the flat GHL contact count this fell back to before.
      if (name === 'total leads') return leadStats.marketedLeads || 0;
    }
    if (name === 'conversion rate') return leadStats.marketedCloseRate || 0;
  }
  // Real HVAC Service call breakdown - user-confirmed definitions: Demand = job type "P1"
  // specifically, Recurring = R1/R2/R3 WeCare visits, Recall (RCL- job types) and Warranty
  // (WARR- job types) are two separate real categories, kept separate per user, not combined.
  // Opp Service Calls = jobs with a linked active estimate ("ones where there are
  // opportunities"). See getServiceCallStats in server/routes/forecast.js. Previously all of
  // these showed the exact same raw department job count regardless of name.
  if (serviceCallStats) {
    if (name === 'demand') return serviceCallStats.demand || 0;
    if (name === 'recurring') return serviceCallStats.recurring || 0;
    if (name.includes('warranty')) return serviceCallStats.warranty || 0;
    if (name.includes('recall')) return serviceCallStats.recall || 0;
    if (name.includes('opp') && name.includes('call')) return serviceCallStats.oppServiceCalls || 0;
    if (name.includes('total call')) return serviceCallStats.totalCalls || 0;
  }
  if (name.includes('lead')) return overallLive?.liveLeads || 0;
  if (name.includes('opportunit')) return overallLive?.liveOpps || 0;
  // Revenue/job-count actuals need a real per-department BU match. Falling back to the
  // company-wide total for a department with no matching BU (previous behavior) showed
  // wildly wrong numbers - e.g. a $50k/mo department budget compared against the whole
  // company's $500k/mo actual. No match means no reliable data, not "use everyone else's".
  if (!deptLive) return 0;
  // Any currency-unit line was returning raw department revenue unconditionally, regardless
  // of the KPI's actual name - so "Revenue", "Avg Ticket", and "RMR" all showed the exact
  // same number (total department revenue) for the same department/month. Avg Ticket needs
  // revenue / completed jobs, not revenue itself - same calculation ForecastTable already
  // uses for its own "Avg Ticket" header stat.
  if (name.includes('avg') || name.includes('average') || name.includes('ticket')) {
    return deptLive.liveCompletedJobs > 0 ? (deptLive.liveRevenue || 0) / deptLive.liveCompletedJobs : 0;
  }
  // "COD Revenue" and "Service Revenue" both previously fell through to the same generic
  // currency branch below (deptLive.liveRevenue), showing identical numbers - user confirmed
  // the real relationship: COD Revenue = job revenue alone, Service Revenue = COD Revenue + RMR
  // (verified against the budget numbers themselves: e.g. $89,238 COD + $78,000 RMR =
  // $167,238 Service Revenue, exactly). deptLive.liveRevenue is job-invoice revenue only - the
  // real ST business unit for RMR/membership billing ("Gold Plan") is a separate bucket from
  // "HVAC - Service." in computeLiveActuals, so it's never double-counted here.
  if (name === 'cod revenue') return deptLive.liveRevenue || 0;
  if (name === 'service revenue') return (deptLive.liveRevenue || 0) + (membershipStats?.mrr || 0);
  // Percent-unit KPIs (conversion rates like "Marketed L2C%", "Attrition") had no matching
  // branch at all, so they fell through to the raw job-count return below and displayed a job
  // count mislabeled as a percentage (e.g. "45" shown as "45%"). There's no reliable way to
  // compute a real conversion rate from this data model (no leads-that-converted vs
  // total-leads breakdown available per BU) - showing 0/no-data is more honest than a wrong
  // number formatted as a percent.
  if (item.unit === 'percent') return 0;
  if (item.unit === 'currency') return deptLive.liveRevenue || 0;
  if (name.includes('scheduled')) return deptLive.liveScheduledJobs || 0;
  if (name.includes('completed')) return deptLive.liveCompletedJobs || 0;
  return deptLive.liveJobCount || 0;
}

// FY month index (1-12, Mar..Feb) -> calendar [startDate, endDate] for that month, used to
// pull real per-month membership stats from /api/memberships (a completely separate,
// already-built system from the ST job/revenue live-actuals data).
function fiscalMonthDateRange(fiscalYear, fm) {
  const FISCAL_CAL_MONTHS = [3,4,5,6,7,8,9,10,11,12,1,2];
  const calMonth = FISCAL_CAL_MONTHS[fm - 1];
  const calYear = calMonth >= 3 ? fiscalYear : fiscalYear + 1;
  const lastDay = new Date(calYear, calMonth, 0).getDate();
  const pad = n => String(n).padStart(2, '0');
  return [`${calYear}-${pad(calMonth)}-01`, `${calYear}-${pad(calMonth)}-${pad(lastDay)}`];
}

// ── KPI Section — mirrors Budget row names, each month shows Budget/Actual/Variance ──
function KpiSection({ fiscalYear: parentFY }) {
  const currentFY = getCurrentFiscalYear();
  const [fiscalYear, setFiscalYear] = useState(parentFY);
  const { items: budgetItems, loading: budgetLoading } = useBudgetItems(fiscalYear);
  const { data: liveData, byBu, loading: liveLoading, error: liveError } = useLiveActuals(fiscalYear);
  const fyOptions = [currentFY + 1, currentFY, currentFY - 1, currentFY - 2];

  // Real per-fiscal-month membership stats (Portfolio Close/Net New Adds/Attrition/RMR).
  // "RMR" lives under the HVAC Service department in the real budget data (not a "Membership"
  // department), so this triggers on either a membership-looking department OR a line literally
  // named "RMR" - gating on department name alone meant RMR never got real data at all and fell
  // through to the generic currency fallback, which is why it showed the exact same number as
  // Service Revenue/COD Revenue (same repeating-numbers bug, different department).
  const [membershipStatsByMonth, setMembershipStatsByMonth] = useState({});
  const hasMembershipDept = useMemo(
    () => budgetItems.some(it =>
      (it.department || '').toLowerCase().includes('membership') ||
      (it.line_name || '').toLowerCase() === 'rmr'
    ),
    [budgetItems]
  );
  useEffect(() => {
    if (!hasMembershipDept || !fiscalYear) return;
    let cancelled = false;
    // One request for the whole fiscal year instead of 12 parallel per-month calls to
    // /api/memberships - that route does a full unfiltered membership scan plus a correlated
    // per-active-customer invoice join on every call, and 12 of those at once regularly timed
    // out for several months, silently falling back to a generic (and wrong) department job
    // count. See server/routes/forecast.js's /membership-stats/:year for the real fix.
    apiClient.get(`/forecast/membership-stats/${fiscalYear}`)
      .then(res => { if (!cancelled) setMembershipStatsByMonth(res.data.byMonth || {}); })
      .catch(() => { if (!cancelled) setMembershipStatsByMonth({}); });
    return () => { cancelled = true; };
  }, [hasMembershipDept, fiscalYear]);

  // Real per-fiscal-month "Marketed Leads"/"Tech Leads" counts + close rates, from
  // /api/forecast/lead-stats (see getLeadStats server-side) - only fetched if a department
  // actually has a leads-named KPI line, same gating pattern as membership stats above.
  const [leadStatsByMonth, setLeadStatsByMonth] = useState({});
  const hasLeadsDept = useMemo(
    () => budgetItems.some(it => (it.line_name || '').toLowerCase().includes('lead')),
    [budgetItems]
  );
  useEffect(() => {
    if (!hasLeadsDept || !fiscalYear) return;
    let cancelled = false;
    // Each of these hits ServiceTitan's Reporting API directly (no single-request/whole-year
    // option - ST aggregates per-BU rows over whatever date range is passed, with no month
    // breakdown), so firing all 12 fiscal months at once risks the same overload/timeout
    // pattern that caused the membership stats bug above. Cap concurrency instead of firing
    // all 12 in parallel.
    // A month's fetch failing (timeout, transient 5xx from ST's Reporting API) previously just
    // meant that month showed blank forever - one retry after the rest of the batch finishes
    // gives a transient failure a second chance instead of permanently dropping that month.
    function fetchMonth(fm) {
      const [startDate, endDate] = fiscalMonthDateRange(fiscalYear, fm);
      return apiClient.get('/forecast/lead-stats', { params: { startDate, endDate }, timeout: 20000 })
        .then(res => [fm, res.data || null])
        .catch(() => [fm, null]);
    }
    async function loadSequenced() {
      const map = {};
      const CONCURRENCY = 3;
      const months = Array.from({ length: 12 }, (_, i) => i + 1);
      const failed = [];
      for (let i = 0; i < months.length && !cancelled; i += CONCURRENCY) {
        const batch = months.slice(i, i + CONCURRENCY);
        const results = await Promise.all(batch.map(fetchMonth));
        for (const [fm, stats] of results) {
          if (stats) map[fm] = stats; else failed.push(fm);
        }
      }
      if (!cancelled && failed.length > 0) {
        const retryResults = await Promise.all(failed.map(fetchMonth));
        for (const [fm, stats] of retryResults) if (stats) map[fm] = stats;
      }
      if (!cancelled) setLeadStatsByMonth(map);
    }
    loadSequenced();
    return () => { cancelled = true; };
  }, [hasLeadsDept, fiscalYear]);

  // Real per-fiscal-month HVAC Service call breakdown (Opp Service Calls/Demand/Recurring/
  // Recall-Warranty/Total Calls) from /api/forecast/service-call-stats - these were all
  // showing the exact same raw department job count (same repeating-numbers bug class as the
  // membership fix above). One request for the whole year, same pattern as membership-stats.
  const [serviceCallStatsByMonth, setServiceCallStatsByMonth] = useState({});
  const SERVICE_CALL_LINE_NAMES = ['opp service calls', 'demand', 'recurring', 'recall', 'warranty', 'total calls'];
  const hasServiceCallDept = useMemo(
    () => budgetItems.some(it => SERVICE_CALL_LINE_NAMES.includes((it.line_name || '').toLowerCase())),
    [budgetItems]
  );
  useEffect(() => {
    if (!hasServiceCallDept || !fiscalYear) return;
    let cancelled = false;
    apiClient.get(`/forecast/service-call-stats/${fiscalYear}`)
      .then(res => { if (!cancelled) setServiceCallStatsByMonth(res.data.byMonth || {}); })
      .catch(() => { if (!cancelled) setServiceCallStatsByMonth({}); });
    return () => { cancelled = true; };
  }, [hasServiceCallDept, fiscalYear]);

  // Map live data to fiscal months (company-wide - only used directly for leads/opps, which
  // have no per-BU breakdown)
  const fiscalMonthMap = useMemo(() => {
    const map = {};
    for (const ld of liveData) {
      let fm;
      if (ld.month >= 3) fm = ld.month - 2;
      else fm = ld.month + 10;
      map[fm] = ld;
    }
    return map;
  }, [liveData]);

  // Per-BU fiscal month maps, same shape as ForecastSection's
  const buFiscalMaps = useMemo(() => {
    if (!byBu) return {};
    const maps = {};
    for (const [buName, buMonths] of Object.entries(byBu)) {
      const map = {};
      for (const ld of buMonths) {
        let fm; if (ld.month >= 3) fm = ld.month - 2; else fm = ld.month + 10;
        map[fm] = ld;
      }
      maps[buName] = map;
    }
    return maps;
  }, [byBu]);

  // Current fiscal month
  const now = new Date();
  const calMonth = now.getMonth() + 1;
  const currentFM = calMonth >= 3 ? calMonth - 2 : calMonth + 10;

  // Group budget items by department
  const { groups, deptOrder } = useMemo(() => groupByDept(budgetItems), [budgetItems]);

  // Each department's own actuals - previously every department showed the same
  // company-wide totals because there was no BU matching here at all (unlike the Forecast
  // tab, which already matches departments to business units). Sum the fiscal-month data
  // for every BU that matches this department name, same matching rule ForecastSection uses.
  const deptFiscalMaps = useMemo(() => {
    const result = {};
    for (const dept of deptOrder) {
      const matchingBus = Object.keys(buFiscalMaps).filter(bu => deptMatchesBuFuzzy(dept, bu));
      if (matchingBus.length === 0) { result[dept] = null; continue; }
      const merged = {};
      for (let fm = 1; fm <= 12; fm++) {
        const combined = { liveRevenue: 0, liveJobCount: 0, liveScheduledJobs: 0, liveCompletedJobs: 0 };
        for (const bu of matchingBus) {
          const ld = buFiscalMaps[bu][fm];
          if (!ld) continue;
          combined.liveRevenue += ld.liveRevenue || 0;
          combined.liveJobCount += ld.liveJobCount || 0;
          combined.liveScheduledJobs += ld.liveScheduledJobs || 0;
          combined.liveCompletedJobs += ld.liveCompletedJobs || 0;
        }
        merged[fm] = combined;
      }
      result[dept] = merged;
    }
    return result;
  }, [deptOrder, buFiscalMaps]);

  if (budgetLoading || liveLoading) return <LoadingSpinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {liveError && <ErrorBanner message={liveError} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
            KPIs — Budget vs Actual vs Variance
          </h3>
          <select value={fiscalYear} onChange={e => setFiscalYear(Number(e.target.value))} style={{ ...st.input, width: 'auto' }}>
            {fyOptions.map(y => <option key={y} value={y}>FY {y} (Mar {y} - Feb {y + 1})</option>)}
          </select>
        </div>
      </div>

      {deptOrder.length === 0 && (
        <div style={st.empty}>No budget data for FY{fiscalYear}. Add departments and line items on the Budget tab first.</div>
      )}

      {deptOrder.map(dept => {
        const items = groups[dept] || [];
        const revenueItems = items.filter(it => it.unit === 'currency');
        const deptLiveMap = deptFiscalMaps[dept];
        const isMembershipDept = dept.toLowerCase().includes('membership');
        // "Service Revenue"/"COD Revenue" both need the department's real RMR (Service Revenue
        // = COD Revenue + RMR, COD Revenue = job revenue alone) alongside whichever line is
        // actually "RMR" - so membershipStats needs to reach every item in a department that
        // has an RMR line, not just the RMR row itself.
        const deptHasRmr = items.some(it => (it.line_name || '').toLowerCase() === 'rmr');
        return (
          <div key={dept} style={st.deptSection}>
            <div style={st.deptHeader}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>{dept}</h3>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{items.length} line items</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ ...st.th, minWidth: '140px', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 2, verticalAlign: 'bottom' }}>KPI</th>
                    {FISCAL_MONTHS.map((m, i) => (
                      <th key={m} colSpan={3} style={{ ...st.th, textAlign: 'center', borderBottom: 'none', background: i + 1 === currentFM ? '#ede9fe' : '#f8fafc' }}>{m}</th>
                    ))}
                    <th colSpan={3} style={{ ...st.th, textAlign: 'center', borderBottom: 'none', fontWeight: '700' }}>TOTAL</th>
                  </tr>
                  <tr>
                    {FISCAL_MONTHS.map((m, i) => (
                      <React.Fragment key={m}>
                        <th style={{ ...st.th, fontSize: '0.55rem', color: '#7c3aed', background: i + 1 === currentFM ? '#ede9fe' : '#faf5ff', padding: '0.2rem 0.35rem' }}>Bud</th>
                        <th style={{ ...st.th, fontSize: '0.55rem', color: '#16a34a', background: i + 1 === currentFM ? '#ede9fe' : '#ecfdf5', padding: '0.2rem 0.35rem' }}>Act</th>
                        <th style={{ ...st.th, fontSize: '0.55rem', color: '#64748b', background: i + 1 === currentFM ? '#ede9fe' : '#f8fafc', padding: '0.2rem 0.35rem' }}>Var</th>
                      </React.Fragment>
                    ))}
                    <th style={{ ...st.th, fontSize: '0.55rem', color: '#7c3aed', background: '#faf5ff', padding: '0.2rem 0.35rem' }}>Bud</th>
                    <th style={{ ...st.th, fontSize: '0.55rem', color: '#16a34a', background: '#ecfdf5', padding: '0.2rem 0.35rem' }}>Act</th>
                    <th style={{ ...st.th, fontSize: '0.55rem', color: '#64748b', padding: '0.2rem 0.35rem' }}>Var</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    // For each fiscal month, get budget from the budget item and actual from live data
                    let totalBudget = 0;
                    let totalActual = 0;
                    let monthsWithBudget = 0;
                    let totalRevenueForAvg = 0;
                    let totalCompletedForAvg = 0;
                    const isRevenue = item.unit === 'currency';
                    // "Avg Ticket" style rows: summing 12 already-averaged monthly values into
                    // a yearly Total is meaningless (a sum of averages isn't a real average).
                    // Recompute the yearly figure properly below instead.
                    const itemNameLower = (item.line_name || '').toLowerCase();
                    const isAvgType = itemNameLower.includes('avg') || itemNameLower.includes('average') || itemNameLower.includes('ticket');

                    return (
                      <tr key={item.id} style={idx % 2 !== 0 ? { background: '#f8fafc' } : {}}>
                        <td style={{ ...st.td, fontWeight: '600', position: 'sticky', left: 0, background: idx % 2 !== 0 ? '#f8fafc' : '#fff', zIndex: 1, fontSize: '0.75rem' }}>
                          {item.line_name}
                          <span style={{ fontSize: '0.6rem', color: '#94a3b8', marginLeft: '0.2rem' }}>
                            {item.unit === 'currency' ? '$' : item.unit === 'percent' ? '%' : '#'}
                          </span>
                        </td>
                        {MONTH_KEYS.map((k, i) => {
                          const fm = i + 1;
                          const budgetVal = Number(item[k]) || 0;
                          const deptLive = deptLiveMap ? deptLiveMap[fm] : null;
                          const overallLive = fiscalMonthMap[fm];
                          // RMR lives under HVAC Service (not a "membership" department) in the
                          // real budget data, so this isn't gated to isMembershipDept alone -
                          // otherwise RMR never gets real data and falls back to the generic
                          // currency branch (same number as Service Revenue/COD Revenue).
                          const membershipStats = (isMembershipDept || deptHasRmr) ? membershipStatsByMonth[fm] : null;
                          const leadStats = leadStatsByMonth[fm] || null;
                          const serviceCallStats = serviceCallStatsByMonth[fm] || null;
                          const actualVal = getActualForItem(item, deptLive, overallLive, membershipStats, leadStats, serviceCallStats);
                          const showActual = fm <= currentFM && actualVal > 0;
                          const variance = showActual ? actualVal - budgetVal : null;
                          totalBudget += budgetVal;
                          if (budgetVal > 0) monthsWithBudget++;
                          if (showActual) totalActual += actualVal;
                          if (isAvgType && deptLive) {
                            totalRevenueForAvg += deptLive.liveRevenue || 0;
                            totalCompletedForAvg += deptLive.liveCompletedJobs || 0;
                          }
                          return (
                            <React.Fragment key={k}>
                              <td style={{ ...st.td, fontSize: '0.7rem', color: '#7c3aed', background: '#faf5ff', padding: '0.25rem 0.3rem' }}>
                                {budgetVal > 0 ? formatVal(budgetVal, item.unit) : '\u2014'}
                              </td>
                              <td style={{ ...st.td, fontSize: '0.7rem', color: showActual ? '#16a34a' : '#cbd5e1', background: '#f0fdf4', padding: '0.25rem 0.3rem', fontWeight: showActual ? '600' : '400' }}>
                                {showActual ? formatVal(actualVal, item.unit) : '\u2014'}
                              </td>
                              <td style={{ ...st.td, fontSize: '0.7rem', padding: '0.25rem 0.3rem', fontWeight: '600', color: variance == null ? '#cbd5e1' : variance >= 0 ? '#16a34a' : '#dc2626' }}>
                                {variance != null ? ((variance >= 0 ? '+' : '') + formatVal(variance, item.unit)) : '\u2014'}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        {(() => {
                          // Avg-ticket rows: a real annual average (total revenue / total
                          // completed jobs across the year), not a sum of 12 monthly averages.
                          const displayTotalBudget = isAvgType
                            ? (monthsWithBudget > 0 ? totalBudget / monthsWithBudget : 0)
                            : totalBudget;
                          const displayTotalActual = isAvgType
                            ? (totalCompletedForAvg > 0 ? totalRevenueForAvg / totalCompletedForAvg : 0)
                            : totalActual;
                          return (
                            <>
                              <td style={{ ...st.td, fontSize: '0.7rem', color: '#7c3aed', fontWeight: '700', background: '#faf5ff', padding: '0.25rem 0.3rem' }}>
                                {formatVal(displayTotalBudget, item.unit)}
                              </td>
                              <td style={{ ...st.td, fontSize: '0.7rem', color: '#16a34a', fontWeight: '700', background: '#f0fdf4', padding: '0.25rem 0.3rem' }}>
                                {displayTotalActual > 0 ? formatVal(displayTotalActual, item.unit) : '\u2014'}
                              </td>
                              <td style={{ ...st.td, fontSize: '0.7rem', fontWeight: '700', padding: '0.25rem 0.3rem', color: displayTotalActual > 0 ? (displayTotalActual - displayTotalBudget >= 0 ? '#16a34a' : '#dc2626') : '#cbd5e1' }}>
                                {displayTotalActual > 0 ? ((displayTotalActual - displayTotalBudget >= 0 ? '+' : '') + formatVal(displayTotalActual - displayTotalBudget, item.unit)) : '\u2014'}
                              </td>
                            </>
                          );
                        })()}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Working Days Section ──────────────────────────────────────────────────────
function WorkingDaysSection({ fiscalYear }) {
  const currentFY = getCurrentFiscalYear();
  const [fy, setFy] = useState(fiscalYear);
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);
  const fyOptions = [currentFY + 1, currentFY, currentFY - 1, currentFY - 2];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/win-the-day/working-days/${fy}`);
      const map = {};
      for (const row of (res.data.workingDays || [])) map[row.month] = row.working_days;
      setDays(map);
    } catch {} finally { setLoading(false); }
  }, [fy]);

  useEffect(() => { load(); }, [load]);

  const saveDays = async (month, value) => {
    const n = parseInt(value);
    if (isNaN(n) || n < 0 || n > 31) return;
    await apiClient.put('/win-the-day/working-days', { fiscal_year: fy, month, working_days: n });
    setDays(prev => ({ ...prev, [month]: n }));
  };

  // Calendar months corresponding to fiscal months: Mar(3)..Feb(2)
  const FISCAL_CAL_MONTHS = [3,4,5,6,7,8,9,10,11,12,1,2];
  const totalDays = FISCAL_MONTHS.reduce((s, _, i) => s + (days[FISCAL_CAL_MONTHS[i]] || 0), 0);

  return (
    <div style={st.deptSection}>
      <div style={{ ...st.deptHeader, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderBottom: '1px solid #fbbf24' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#92400e' }}>Working Days per Month</h3>
          <select value={fy} onChange={e => setFy(Number(e.target.value))} style={{ ...st.input, width: 'auto' }}>
            {fyOptions.map(y => <option key={y} value={y}>FY {y}</option>)}
          </select>
        </div>
        <span style={{ fontSize: '0.8rem', color: '#92400e', fontWeight: '600' }}>Total: {totalDays} days</span>
      </div>
      {loading ? <div style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8' }}>Loading...</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={st.table}>
            <thead>
              <tr>
                {FISCAL_MONTHS.map(m => <th key={m} style={st.th}>{m}</th>)}
                <th style={{ ...st.th, fontWeight: '700' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {FISCAL_MONTHS.map((_, i) => {
                  const calMonth = FISCAL_CAL_MONTHS[i];
                  return (
                    <td key={i} style={st.td}>
                      <EditableCell
                        value={days[calMonth]}
                        unit="number"
                        onSave={v => saveDays(calMonth, v)}
                        style={{ width: '50px' }}
                      />
                    </td>
                  );
                })}
                <td style={{ ...st.td, fontWeight: '700' }}>{totalDays}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// BU tables to merge into one combined table on the Forecast tab, keyed by the target
// (display) label. Source names include the real raw ST business unit names confirmed live
// (e.g. "HVAC - Service." with a trailing period, "Gold Plans" plural, "HVAC - Sales") plus
// a couple of plausible alternate spellings/formats in case another account differs.
const COMBINE_GROUPS = {
  'HVAC Service & Gold Plans': ['HVAC - Service.', 'HVAC - Service', 'HVAC Service', 'Gold Plans', 'Gold Plan'],
  'Comfort Advisors': ['HVAC - Sales', 'HVAC Sales', 'Comfort Advisors'],
  'HVAC Install & Install': ['HVAC - Install', 'HVAC Install', 'Install', 'Installs'],
  // Francis's account has a separate "Plumbing - Service" BU alongside plain "Plumbing" -
  // fold them into one table.
  'Plumbing': ['Plumbing - Service', 'Plumbing'],
};

// ── BU name to department mapping ─────────────────────────────────────────────
const BU_DEPT_MAP = {
  'installs': ['Install'],
  'hvac service': ['HVAC Service', 'HVAC Comfort Advisors Team', 'HVAC Marketed Leads'],
  'plumbing': ['Plumbing'],
  'electrical': ['Electrical'],
  'memberships': ['Membership'],
  'comfort advisors': ['HVAC Comfort Advisors Team', 'Comfort Advisors Team'],
  'operations': [],
};

function deptMatchesBu(deptName, buName) {
  if (!buName) return true;
  const key = buName.toLowerCase();
  const mapped = BU_DEPT_MAP[key];
  if (mapped && mapped.length > 0) return mapped.includes(deptName);
  return deptName.toLowerCase().includes(key);
}

// BU_DEPT_MAP requires an exact string match against whatever a department was actually
// typed in on the Budget tab (e.g. "HVAC Service" must match one of the mapped names
// character-for-character), and doesn't even have an entry for "Gold Plan" - real ST business
// unit names never went through that curated list to confirm they'd actually match. This is a
// looser matcher used specifically for pulling real per-department actuals onto the KPI tab:
// normalized (lowercased, non-alphanumeric stripped) substring match in either direction, plus
// a small alias list for known BU/department naming mismatches (ST's membership billing BU is
// named "Gold Plan", not "Membership").
const DEPT_BU_ALIASES = { 'gold plan': ['membership'] };
function normalizeForMatch(s) { return (s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
function deptMatchesBuFuzzy(deptName, buName) {
  const d = normalizeForMatch(deptName);
  const b = normalizeForMatch(buName);
  if (!d || !b) return false;
  if (d.includes(b) || b.includes(d)) return true;
  const aliases = DEPT_BU_ALIASES[(buName || '').toLowerCase()] || [];
  return aliases.some(a => d.includes(normalizeForMatch(a)));
}

// ── Main Page ────────────────────────────────────────────────────────────────
// ── Budget upload ────────────────────────────────────────────────────────────
// Reads the brand's sheet from an .xlsx/.csv budget workbook (server side), shows what each row
// would update or create in this brand's budget, and saves only the rows left ticked.
function BudgetUpload({ fiscalYear, onImport, onClose }) {
  const [file, setFile] = useState(null); // { name, base64 }
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');
  const [showSkipped, setShowSkipped] = useState(false);

  const runPreview = async (f, sheet) => {
    setBusy(true); setErr(''); setDone('');
    try {
      const res = await apiClient.post(`/forecast/budget-items/import/preview/${fiscalYear}`, { file: f.base64, filename: f.name, sheet: sheet || undefined });
      setPreview(res.data);
      const sel = {};
      (res.data.plan || []).forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
    } catch (e) {
      setPreview(null);
      setErr(e.response?.data?.error || e.message || 'Could not read that file');
    } finally { setBusy(false); }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result).split(',')[1] || '';
      const next = { name: f.name, base64 };
      setFile(next);
      runPreview(next);
    };
    reader.onerror = () => setErr('Could not read that file');
    reader.readAsDataURL(f);
  };

  const plan = preview?.plan || [];
  const chosen = plan.filter((_, i) => selected[i]);
  const annual = (p) => MONTH_KEYS.reduce((s, k) => s + (Number(p[k]) || 0), 0);
  const fmt = (p, v) => (p.unit === 'currency' ? formatCurrency(v) : p.unit === 'percent' ? `${Math.round(v * 10) / 10}%` : Math.round(v * 10) / 10);

  const doImport = async () => {
    setBusy(true); setErr('');
    try {
      const items = chosen.map(({ action, from, ...item }) => item);
      await onImport(items);
      setDone(`Imported ${items.length} line${items.length === 1 ? '' : 's'} into FY${fiscalYear}.`);
      setPreview(null); setFile(null);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: '700', color: '#1e293b' }}>Upload budget - FY{fiscalYear} (Mar {fiscalYear} - Feb {fiscalYear + 1})</div>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Excel (.xlsx) or CSV. Uses the sheet named after the brand you're viewing. Nothing is saved until you click Import.</div>
        </div>
        <button onClick={onClose} style={st.cancelBtn}>Close</button>
      </div>

      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="file" accept=".xlsx,.csv" onChange={pickFile} disabled={busy} />
        {preview?.sheets?.length > 1 && file && (
          <label style={st.label}>Sheet{' '}
            <select value={preview.sheet || ''} onChange={e => runPreview(file, e.target.value)} disabled={busy} style={{ ...st.input, width: 'auto' }}>
              {!preview.sheet && <option value="">Choose a sheet</option>}
              {preview.sheets.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}
        {busy && <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Working...</span>}
      </div>

      {err && <div style={{ marginTop: '0.5rem', color: '#dc2626', fontSize: '0.85rem' }}>{err}</div>}
      {done && <div style={{ marginTop: '0.5rem', color: '#16a34a', fontSize: '0.85rem', fontWeight: '600' }}>{done}</div>}
      {preview?.needSheet && <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#92400e' }}>No sheet is named "{preview.brand}" - choose which sheet to use.</div>}

      {plan.length > 0 && (
        <>
          <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#475569' }}>
            Sheet <b>{preview.sheet}</b> for <b>{preview.brand}</b>: {plan.filter(p => p.action === 'update').length} lines update existing budget lines, {plan.filter(p => p.action === 'create').length} are new.
          </div>
          <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#64748b' }}>
                  <th style={{ padding: '0.3rem' }}><input type="checkbox" checked={chosen.length === plan.length} onChange={e => { const s = {}; plan.forEach((_, i) => { s[i] = e.target.checked; }); setSelected(s); }} /></th>
                  <th style={{ padding: '0.3rem' }}>Action</th>
                  <th style={{ padding: '0.3rem' }}>Department / Line</th>
                  <th style={{ padding: '0.3rem' }}>From spreadsheet</th>
                  <th style={{ padding: '0.3rem', textAlign: 'right' }}>Year total</th>
                  <th style={{ padding: '0.3rem', textAlign: 'right' }}>Prior year</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((p, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f1f5f9', opacity: selected[i] ? 1 : 0.45 }}>
                    <td style={{ padding: '0.3rem' }}><input type="checkbox" checked={!!selected[i]} onChange={e => setSelected(s => ({ ...s, [i]: e.target.checked }))} /></td>
                    <td style={{ padding: '0.3rem' }}>
                      <span style={{ padding: '0.1rem 0.45rem', borderRadius: '999px', fontWeight: '600', fontSize: '0.7rem', background: p.action === 'update' ? '#dbeafe' : '#dcfce7', color: p.action === 'update' ? '#1d4ed8' : '#15803d' }}>
                        {p.action === 'update' ? 'Update' : 'New'}
                      </span>
                    </td>
                    <td style={{ padding: '0.3rem', fontWeight: '600', color: '#1e293b' }}>{p.department} / {p.line_name}</td>
                    <td style={{ padding: '0.3rem', color: '#64748b' }}>row {p.from.row}: {p.from.label}</td>
                    <td style={{ padding: '0.3rem', textAlign: 'right' }}>{p.unit === 'percent' ? '-' : fmt(p, annual(p))}</td>
                    <td style={{ padding: '0.3rem', textAlign: 'right' }}>{p.prior_year ? fmt(p, Number(p.prior_year)) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.skipped?.length > 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#64748b' }}>
              <button onClick={() => setShowSkipped(v => !v)} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '0.75rem' }}>
                {showSkipped ? 'Hide' : 'Show'} {preview.skipped.length} skipped rows
              </button>
              {showSkipped && (
                <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.2rem' }}>
                  {preview.skipped.map((s, i) => <li key={i}>row {s.row}: {s.label} - {s.reason}</li>)}
                </ul>
              )}
            </div>
          )}
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={doImport} disabled={busy || chosen.length === 0} style={st.addBtn}>Import {chosen.length} line{chosen.length === 1 ? '' : 's'}</button>
            <button onClick={onClose} style={st.cancelBtn}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function BudgetForecastPage() {
  const currentFY = getCurrentFiscalYear();
  const [fiscalYear, setFiscalYear] = useState(currentFY);
  const [deptFilter, setDeptFilter] = useState('all');
  const [showAddDept, setShowAddDept] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { selectedBu } = useBu();

  const { items, loading, error, addItem, updateItem, deleteItem, bulkSave } = useBudgetItems(fiscalYear);

  // Load BU names for filtering
  const [buUnits, setBuUnits] = useState([]);
  React.useEffect(() => {
    import('../api/apiClient').then(mod => {
      mod.default.get('/business-units').then(res => setBuUnits(res.data.units || [])).catch(() => {});
    });
  }, []);

  const selectedBuName = useMemo(() => {
    if (!selectedBu) return null;
    const unit = buUnits.find(u => u.id === selectedBu);
    return unit ? unit.name : null;
  }, [selectedBu, buUnits]);

  const { groups, deptOrder } = useMemo(() => groupByDept(items), [items]);

  const filteredDepts = useMemo(() => {
    let depts = deptFilter === 'all' ? deptOrder : deptOrder.filter(d => d === deptFilter);
    if (selectedBuName) depts = depts.filter(d => deptMatchesBu(d, selectedBuName));
    return depts;
  }, [deptFilter, deptOrder, selectedBuName]);

  const fyOptions = [currentFY + 1, currentFY, currentFY - 1, currentFY - 2];
  const TABS = ['Budget', 'Forecast', 'KPIs', 'Working Days'];

  // Grand totals
  const revenueItems = items.filter(it => it.unit === 'currency');
  const grandTotal = revenueItems.reduce((s, it) => s + getRowTotal(it), 0);
  const grandPY = revenueItems.reduce((s, it) => s + (Number(it.prior_year) || 0), 0);
  const grandMonthTotals = MONTH_KEYS.map(k => revenueItems.reduce((s, it) => s + (Number(it[k]) || 0), 0));

  return (
    <div>
      <Header title="Budget / Forecast" />
      <div style={st.page}>
        {error && <ErrorBanner message={error} />}

        {/* Controls */}
        <div style={st.controls}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div>
              <label style={st.label}>Fiscal Year</label>
              <select value={fiscalYear} onChange={e => setFiscalYear(Number(e.target.value))} style={{ ...st.input, width: 'auto', marginLeft: '0.5rem' }}>
                {fyOptions.map(y => <option key={y} value={y}>{y} (Mar {y} - Feb {y + 1})</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>Department</label>
              <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={{ ...st.input, width: 'auto', marginLeft: '0.5rem' }}>
                <option value="all">All Departments</option>
                {deptOrder.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <button onClick={() => setShowAddDept(true)} style={st.addBtn}>+ Department</button>
            <button onClick={() => setShowUpload(v => !v)} style={st.addBtn}>Upload budget</button>
          </div>
          {grandTotal > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Total Budget</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b' }}>{formatCurrency(grandTotal)}</div>
            </div>
          )}
        </div>

        {showAddDept && (
          <AddDeptForm fiscalYear={fiscalYear} onAdd={addItem} onCancel={() => setShowAddDept(false)} />
        )}

        {showUpload && (
          <BudgetUpload fiscalYear={fiscalYear} onImport={bulkSave} onClose={() => setShowUpload(false)} />
        )}

        {/* Tabs */}
        <div style={st.tabBar}>
          {TABS.map((tab, i) => (
            <button key={tab} onClick={() => setActiveTab(i)} style={{ ...st.tabBtn, ...(activeTab === i ? st.tabBtnActive : {}) }}>{tab}</button>
          ))}
        </div>

        {loading ? <LoadingSpinner /> : (
          <>
            {activeTab === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filteredDepts.length === 0 && (
                  <div style={st.empty}>
                    No budget data for FY{fiscalYear}. Click "+ Department" to start building your budget.
                  </div>
                )}
                {filteredDepts.map(dept => (
                  <DepartmentSection
                    key={dept}
                    department={dept}
                    items={groups[dept]}
                    fiscalYear={fiscalYear}
                    onUpdateItem={updateItem}
                    onAddItem={addItem}
                    onDeleteItem={deleteItem}
                  />
                ))}

                {/* Grand Total Revenue Row */}
                {grandTotal > 0 && (
                  <div style={{ background: '#1e293b', borderRadius: '0.75rem', padding: '0.75rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ ...st.table, color: '#fff' }}>
                        <thead>
                          <tr>
                            <th style={{ ...st.th, background: 'transparent', color: '#94a3b8', minWidth: '150px' }}>TOTAL REVENUE</th>
                            {FISCAL_MONTHS.map(m => <th key={m} style={{ ...st.th, background: 'transparent', color: '#94a3b8' }}>{m}</th>)}
                            <th style={{ ...st.th, background: 'transparent', color: '#94a3b8', fontWeight: '700' }}>Total</th>
                            <th style={{ ...st.th, background: 'transparent', color: '#94a3b8' }}>PY</th>
                            <th style={{ ...st.th, background: 'transparent', color: '#94a3b8' }}>$ Chg</th>
                            <th style={{ ...st.th, background: 'transparent', color: '#94a3b8' }}>% Chg</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ ...st.td, fontWeight: '800', color: '#fff', borderBottom: 'none' }}>All Departments</td>
                            {grandMonthTotals.map((v, i) => (
                              <td key={i} style={{ ...st.td, fontWeight: '700', color: '#a5f3fc', borderBottom: 'none' }}>{formatCurrency(v)}</td>
                            ))}
                            <td style={{ ...st.td, fontWeight: '800', color: '#22c55e', borderBottom: 'none', fontSize: '0.9rem' }}>{formatCurrency(grandTotal)}</td>
                            <td style={{ ...st.td, fontWeight: '600', color: '#cbd5e1', borderBottom: 'none' }}>{formatCurrency(grandPY)}</td>
                            <td style={{ ...st.td, fontWeight: '700', color: (grandTotal - grandPY) >= 0 ? '#22c55e' : '#f87171', borderBottom: 'none' }}>
                              {formatCurrency(grandTotal - grandPY)}
                            </td>
                            <td style={{ ...st.td, fontWeight: '700', color: grandPY > 0 ? ((grandTotal - grandPY) / grandPY * 100 >= 0 ? '#22c55e' : '#f87171') : '#94a3b8', borderBottom: 'none' }}>
                              {grandPY > 0 ? ((grandTotal - grandPY) / grandPY * 100 >= 0 ? '+' : '') + ((grandTotal - grandPY) / grandPY * 100).toFixed(1) + '%' : '\u2014'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 1 && (
              <ForecastSection fiscalYear={fiscalYear} budgetItems={items} />
            )}

            {activeTab === 2 && (
              <KpiSection fiscalYear={fiscalYear} />
            )}

            {activeTab === 3 && (
              <WorkingDaysSection fiscalYear={fiscalYear} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const st = {
  page: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  controls: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem',
    background: '#fff', borderRadius: '0.75rem', padding: '1rem 1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  tabBar: {
    display: 'flex', gap: '0.25rem', background: '#fff', padding: '0.375rem',
    borderRadius: '0.625rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', flexWrap: 'wrap',
  },
  tabBtn: {
    padding: '0.5rem 1rem', background: 'transparent', border: 'none', borderRadius: '0.5rem',
    cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', color: '#64748b', transition: 'all 0.15s', whiteSpace: 'nowrap',
  },
  tabBtnActive: { background: '#6366f1', color: '#fff' },
  label: { fontSize: '0.8rem', fontWeight: '600', color: '#374151' },
  input: {
    padding: '0.4rem 0.625rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem',
    fontSize: '0.8rem', outline: 'none', color: '#1e293b',
  },
  addBtn: {
    padding: '0.4rem 0.875rem', background: '#6366f1', color: '#fff', border: 'none',
    borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600',
  },
  cancelBtn: {
    padding: '0.4rem 0.75rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: '#475569',
  },
  smallBtn: {
    padding: '0.25rem 0.625rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', color: '#475569',
  },
  deptSection: {
    background: '#fff', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  deptHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.875rem 1.25rem', cursor: 'pointer',
    background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
    borderBottom: '1px solid #c7d2fe',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
  th: {
    padding: '0.5rem 0.625rem', background: '#f8fafc', color: '#64748b', fontWeight: '600',
    fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right',
    borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.35rem 0.625rem', borderBottom: '1px solid #f1f5f9', color: '#1e293b',
    verticalAlign: 'middle', textAlign: 'right', fontSize: '0.8rem',
  },
  cellInput: {
    width: '70px', padding: '0.2rem 0.375rem', border: '1.5px solid #6366f1',
    borderRadius: '0.25rem', fontSize: '0.8rem', outline: 'none', textAlign: 'right',
  },
  cellVal: { padding: '0.2rem 0', minWidth: '60px', borderRadius: '0.2rem' },
  empty: {
    padding: '3rem', textAlign: 'center', color: '#94a3b8', background: '#fff',
    borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  forecastSection: {
    background: '#fff', borderRadius: '0.75rem', padding: '1.25rem 1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  forecastKpi: {
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.5rem',
    padding: '0.75rem 1rem', minWidth: '140px',
  },
  forecastKpiLabel: { fontSize: '0.7rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', marginBottom: '0.25rem' },
  forecastKpiVal: { fontSize: '1.1rem', fontWeight: '800', color: '#1e293b' },
};
